import React, { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { useLang } from '../i18n/LanguageContext';
import { useCheckout } from '../CheckoutContext';
import offer, {
  CALL_PORTRAIT,
  callMinutes,
  formatCount,
  isFilled,
  percentLabel,
  reviewLine,
} from '../config/offer';
import { formatCallSlot, loadCallWindow, saveCallWindow } from '../lib/callSlots';
import { usePricing } from '../lib/pricing';
import { SolanaPayButton } from './SolanaPay';
import COPY from './openBroV60Copy';
import OpenBroFullFilm from './OpenBroFullFilm';
import './OpenBroHome.css';
import './OpenBroV51.css';
import './OpenBroV52.css';
import './OpenBroV60.css';

/**
 * /v60 (et / depuis le 28/10/2026) : la /v52 (hero, puis la vidéo juste dessous) avec les 6 modules sous la vidéo, et sans créneau à choisir
 * avant de payer : paiement direct, le créneau se choisit après (SuccessPage.jsx). Textes : openBroV60Copy.js.
 *
 * OpenBro long sales page.
 * Rythme inspiré de jointherealworld.com : texte bicolore, deux chemins, bento,
 * téléphone, vidéo + deux issues, mur de messages façon Telegram, campus, choix final avec le prix.
 * Visuels : photos de public/ et public/v10, captures de l'espace membre (public/product),
 * reels UGC (public/ugc). Prix et preuve sociale viennent de src/config/offer.js.
 */

const SUPPORT_EMAIL = 'mia@openpro.ai';

/** Film du haut, par langue. Fichiers dans public/, sous 25 Mo (limite Workers). */
const FILM = { fr: '/openbro-film-fr.mp4', en: '/openbro-film-en.mp4' };
const FILM_POSTER = { fr: '/openbro-film-fr.jpg', en: '/openbro-film-en.jpg' };

const FORMATIONS = [
  { id: 'musculation', src: '/v10/card-abdos.jpg', focus: 'center 40%' },
  { id: 'nutrition', src: '/formations/ig-food.jpg', focus: 'center 68%' },
  { id: 'reseaux', src: '/v10/card-ecommerce-cash.jpg', focus: 'center 45%', verified: true },
  { id: 'startup', src: '/v10/card-startup-ycbro.jpg', focus: '30% 40%' },
  { id: 'montage', src: '/formations/ig-content.mp4', poster: '/formations/ig-content.jpg', video: true, focus: 'center 36%' },
  { id: 'ia', src: '/v10/card-ia-money.jpg', focus: 'center 60%' },
];

// Reels 9:16 Seedance 2.5 (source : EXPLO/ugc-campus-food/final2, ré-encodés 720p). Un thème par personne.
const STORIES = [
  { id: 'adam', src: '/ugc/15-adam-23-salle.mp4', poster: '/ugc/15-adam-23-salle.jpg', srcEn: '/ugc/15-adam-23-salle-en.mp4', posterEn: '/ugc/15-adam-23-salle-en.jpg', name: 'Adam', age: 23 },
  { id: 'ines', src: '/ugc/12-ines-24-assiette.mp4', poster: '/ugc/12-ines-24-assiette.jpg', srcEn: '/ugc/12-ines-24-assiette-en.mp4', posterEn: '/ugc/12-ines-24-assiette-en.jpg', name: 'Inès', age: 24 },
  { id: 'theo', src: '/ugc/16-theo-25-business.mp4', poster: '/ugc/16-theo-25-business.jpg', srcEn: '/ugc/16-theo-25-business-en.mp4', posterEn: '/ugc/16-theo-25-business-en.jpg', name: 'Théo', age: 25 },
  { id: 'lucas', src: '/ugc/14-lucas-21-vie.mp4', poster: '/ugc/14-lucas-21-vie.jpg', srcEn: '/ugc/14-lucas-21-vie-en.mp4', posterEn: '/ugc/14-lucas-21-vie-en.jpg', name: 'Lucas', age: 21 },
];

// Mur façon « Our Students Are Winning » : cartes Telegram (mises en situation, cf. FAQ)
// mélangées aux preuves que Gaut avait déjà (studentWinsData.wins de mock.js, copiées dans public/v10/wins).
const WINS = [
  { id: 'yanis', name: 'Yanis', avatar: '/avatars/young-1.jpg', time: '07:12', color: '#e17076', photo: '/v10/result-salle.jpg', reactions: [['💪', 11], ['🔥', 7], ['G', 4]] },
  { shot: '/v10/wins/win-15.jpg' },
  { shot: '/v10/wins/win-04.jpg' },
  { id: 'sarah', name: 'Sarah', avatar: '/avatars/young-2.jpg', time: '12:41', color: '#7bc862', photo: '/v10/result-chirashi-saumon.jpg', reactions: [['😂', 12], ['🔥', 6], ['G2', 9]] },
  { shot: '/v10/wins/win-07.jpg' },
  { id: 'enzo', name: 'Enzo', avatar: '/avatars/young-5.jpg', time: '23:04', color: '#65aadd', reactions: [['🤝', 6], ['🔥', 2], ['G2', 11]] },
  { shot: '/v10/wins/win-08.jpg' },
  { shot: '/v10/wins/win-01.jpg' },
  { id: 'sami', name: 'Julien', avatar: '/avatars/member-4.jpg', time: '18:05', color: '#faa774', photo: '/v10/home-result-sami.jpg', reactions: [['🔥', 9], ['🚀', 12], ['G', 5]] },
  { shot: '/v10/wins/win-12.jpg' },
  { shot: '/v10/wins/win-14.jpg' },
  { shot: '/v10/wins/win-02.jpg' },
  // RS3 8Y réelle : Alexander Migl, CC BY-SA 4.0, Wikimedia « Audi RS3 8Y 1X7A6092 ».
  { id: 'maxime', name: 'Maxime', avatar: '/avatars/member-2.jpg', time: '21:47', color: '#a695e7', photo: '/v10/home-result-rs3.jpg', reactions: [['😮‍💨', 7], ['🔥', 11], ['G2', 4]] },
  { shot: '/v10/wins/win-11.jpg' },
  { shot: '/v10/wins/win-03.jpg' },
  { shot: '/v10/wins/win-06.jpg' },
  { shot: '/v10/wins/win-09.jpg' },
  { shot: '/v10/wins/win-10.jpg' },
  { shot: '/v10/wins/win-05.jpg' },
];

// Bloc « plan alimentaire » du bas : vrais plats des reels de Gaut (mêmes fichiers que MEALS dans OpenBroEat.jsx).
const EAT_PLAN_PHOTOS = [
  { src: '/eat/burger.jpg', pos: 'center 55%' },
  { src: '/eat/pancake-stack.jpg', pos: 'center 60%' },
  { src: '/eat/taco-bowl.jpg', pos: 'center 62%' },
];

// Couvertures « CAMPUS » de l'espace membre (Dashboard.jsx), copiées en local. Leçons : src/data/formations.js.
const CAMPUS = [
  { id: 'musculation', src: '/v10/campus-musculation.jpg', srcEn: '/v10/campus-musculation-en.jpg?v=2', lessons: 31 },
  { id: 'reseaux', src: '/v10/campus-reseaux.jpg', srcEn: '/v10/campus-reseaux-en.jpg?v=2', lessons: 18 },
  { id: 'montage', src: '/v10/campus-montage.jpg', lessons: 16 },
  { id: 'ia', src: '/v10/campus-ia.jpg', srcEn: '/v10/campus-ia-en.jpg?v=2', lessons: 12 },
  { id: 'nutrition', src: '/v10/campus-nutrition.jpg', lessons: 16 },
  { id: 'filming', src: '/v10/campus-filming.jpg', lessons: 16 },
];

function Icon({ name }) {
  const paths = {
    call: 'M7 4h3l1 4-2 1a12 12 0 0 0 6 6l1-2 4 1v3a2 2 0 0 1-2 2A16 16 0 0 1 5 6a2 2 0 0 1 2-2z',
    plan: 'M9 6h11M9 12h11M9 18h11M4 6l1 1 2-2M4 12l1 1 2-2M4 18l1 1 2-2',
    book: 'M4 5.5A1.5 1.5 0 0 1 5.5 4H11v16H5.5A1.5 1.5 0 0 1 4 18.5zM20 5.5A1.5 1.5 0 0 0 18.5 4H13v16h5.5a1.5 1.5 0 0 0 1.5-1.5z',
    streak: 'M4 19h16M6 16v-3M10 16V9M14 16v-5M18 16V6',
    flame: 'M12 21a6 6 0 0 0 6-6c0-4-3-6-4-10-2 2-2.5 4-2.5 5.5C10 9 9 8 9 6c-2 2-3 5-3 9a6 6 0 0 0 6 6z',
    check: 'M5 12.5l4.5 4.5L19 7.5',
    cross: 'M7 7l10 10M17 7L7 17',
    lock: 'M8 11V8a4 4 0 0 1 8 0v3M6.5 11h11v9h-11z',
    arrow: 'M5 12h14M13 6l6 6-6 6',
    plus: 'M12 5v14M5 12h14',
    play: 'M9 6.5v11l9-5.5z',
    pause: 'M7 5h3.5v14H7zM13.5 5H17v14h-3.5z',
    clock: 'M12 3.5a8.5 8.5 0 1 0 0 17 8.5 8.5 0 0 0 0-17zM12 7.5V12l3 2',
    globe: 'M12 3.5a8.5 8.5 0 1 0 0 17 8.5 8.5 0 0 0 0-17zM3.5 12h17M12 3.5c2.2 2.4 2.2 14.6 0 17M12 3.5c-2.2 2.4-2.2 14.6 0 17',
  };
  return (
    <svg className="oh-icon" viewBox="0 0 24 24" aria-hidden="true">
      <path d={paths[name]} />
    </svg>
  );
}

/** Texte bicolore : `**mot**` en blanc appuyé, le reste hérite du gris du bloc.
 *  `slot` (le temps restant) devient un bouton souligné qui réserve le créneau. */
function Rich({ text, slot, onSlot, slotDisabled }) {
  const withSlot = (chunk, key) => {
    if (!slot || !onSlot || !chunk.includes(slot)) return chunk;
    const pieces = chunk.split(slot);
    return pieces.map((piece, index) => (
      <React.Fragment key={`${key}-${index}`}>
        {piece}
        {index < pieces.length - 1 && (
          <button type="button" className="oh-slot" disabled={slotDisabled} onClick={onSlot}>{slot}</button>
        )}
      </React.Fragment>
    ));
  };
  return text.split('\n').map((line, row) => (
    <React.Fragment key={row}>
      {row > 0 && <br />}
      {line.split('**').map((part, index) => (
        index % 2 ? <strong key={index}>{withSlot(part, index)}</strong> : <React.Fragment key={index}>{withSlot(part, index)}</React.Fragment>
      ))}
    </React.Fragment>
  ));
}

/** Réservable jusqu'à 1 h 30 avant l'heure du créneau (même règle que lib/callSlots). */
const BOOKING_LEAD_MS = 90 * 60 * 1000;

/** « 59 min 12 s » / « 2 h 14 min 03 s » / « 1 j 3 h » ; `fmt.tight` colle l'unité (« 2h 14m 03s », usage US).
 *  null si la limite est passée. */
function timeLeft(ms, fmt) {
  if (!Number.isFinite(ms) || ms <= 0) return null;
  const total = Math.floor(ms / 1000);
  const days = Math.floor(total / 86400);
  const hours = Math.floor((total % 86400) / 3600);
  const mins = Math.floor((total % 3600) / 60);
  const secs = total % 60;
  const sep = fmt.tight ? '' : ' ';
  const unit = (n, u) => `${n}${sep}${u}`;
  const sec = fmt.sec || 's';
  if (days) return `${unit(days, fmt.day)} ${unit(hours, fmt.hour)}`;
  const ss = String(secs).padStart(2, '0');
  if (hours) return `${unit(hours, fmt.hour)} ${unit(String(mins).padStart(2, '0'), fmt.min)} ${unit(ss, sec)}`;
  if (mins) return `${unit(mins, fmt.min)} ${unit(ss, sec)}`;
  return unit(secs, sec);
}

function Pill({ children, icon }) {
  return <p className="oh-pill">{icon && <Icon name={icon} />}{children}</p>;
}

/** Logos de paiement : « CB » (carte bancaire française) en euros, Amex en dollars (US et Canada). */
function PayMarks({ currency }) {
  return (
    <span className="oh-marks" aria-hidden="true">
      <span>Apple Pay</span>
      <span>VISA</span>
      <span>Mastercard</span>
      <span>{currency === 'eur' ? 'CB' : 'Amex'}</span>
    </span>
  );
}

function Proof({ lang, copy }) {
  const reviews = reviewLine(lang);
  const avatars = Array.isArray(offer.memberAvatars) ? offer.memberAvatars.filter(Boolean) : [];
  const showMembers = isFilled(offer.payingMembers);
  const showFollowers = !showMembers && isFilled(offer.socialFollowers);
  if (!reviews && !showMembers && !showFollowers && avatars.length === 0) return null;
  const count = showMembers ? offer.payingMembers : offer.socialFollowers;
  return (
    <div className="oh-proof">
      {avatars.length > 0 && (
        <span className="oh-faces">
          {avatars.map((src) => <img key={src} src={src} alt="" loading="lazy" />)}
        </span>
      )}
      <span>
        {(showMembers || showFollowers) && <strong>{formatCount(count, lang)} {showMembers ? copy.membersWord : copy.followersLabel}</strong>}
        {reviews && <em>{reviews}</em>}
      </span>
    </div>
  );
}

/** Prix dans la devise facturée (lib/pricing.js). Prix barré seulement en euros, et seulement s'il est réel. */
function PriceTag({ copy, pricing, big }) {
  const { amount, previous, money } = pricing;
  const discount = previous !== null ? Math.floor(((previous - amount) / previous) * 100) : null;
  return (
    <span className={big ? 'oh-price oh-price-big' : 'oh-price'}>
      <strong>{money(amount)}</strong>
      <em>{copy.period}</em>
      {previous !== null && <s>{money(previous)}</s>}
      {discount !== null && <b>-{percentLabel(discount)}</b>}
    </span>
  );
}

function FlagFR() {
  return (
    <svg className="oh-flag" viewBox="0 0 3 2" role="img" aria-label="France">
      <rect width="1" height="2" fill="#0055a4" />
      <rect x="1" width="1" height="2" fill="#fff" />
      <rect x="2" width="1" height="2" fill="#ef4135" />
    </svg>
  );
}

function VerifiedBadge({ label }) {
  return (
    <svg className="oh-verified" viewBox="0 0 24 24" role="img" aria-label={label}>
      <path fill="#0095f6" d="M12.00 0.00 L14.07 1.60 L16.59 0.91 L17.89 3.19 L20.49 3.51 L20.81 6.11 L23.09 7.41 L22.40 9.93 L24.00 12.00 L22.40 14.07 L23.09 16.59 L20.81 17.89 L20.49 20.49 L17.89 20.81 L16.59 23.09 L14.07 22.40 L12.00 24.00 L9.93 22.40 L7.41 23.09 L6.11 20.81 L3.51 20.49 L3.19 17.89 L0.91 16.59 L1.60 14.07 L0.00 12.00 L1.60 9.93 L0.91 7.41 L3.19 6.11 L3.51 3.51 L6.11 3.19 L7.41 0.91 L9.93 1.60Z" />
      <path fill="none" stroke="#fff" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" d="M7.4 12.3l3.1 3.1 6.1-6.4" />
    </svg>
  );
}

/** Vrai iMac 24" argent (photo CC0) détouré, qui flotte dans la page ; écran de l'espace membre flouté. */
function VaultScreen({ copy, onPay, disabled }) {
  return (
    <figure className="oh-imac">
      <button type="button" className="oh-imac-hit" disabled={disabled} onClick={onPay} aria-label={copy.vaultLock}>
        <img src="/v10/imac-float.webp" alt="" />
        <span className="oh-imac-pill"><Icon name="lock" />{copy.vaultLock}</span>
      </button>
    </figure>
  );
}

/** iPhone en studio (façon university.com), écran = réservation réelle (ugc-campus-food/build_devices_v11.py). */
function PhoneShot({ alt }) {
  return (
    <figure className="oh-phone-studio">
      <img src="/v10/home-iphone.jpg" alt={alt} loading="lazy" decoding="async" />
    </figure>
  );
}

/** Cartes vidéo 9:16 façon university.com : muettes, en boucle, lues seulement quand la rangée est visible. */
/** « il y a 2 h » / « 2 h ago » à partir d'une date ISO. */
function agoLabel(iso, lang, now = Date.now()) {
  const minutes = Math.max(1, Math.round((now - Date.parse(iso)) / 60000));
  const rtf = new Intl.RelativeTimeFormat(lang === 'en' ? 'en' : 'fr', { numeric: 'always', style: 'short' });
  if (minutes < 60) return rtf.format(-minutes, 'minute');
  if (minutes < 24 * 60) return rtf.format(-Math.round(minutes / 60), 'hour');
  return rtf.format(-Math.round(minutes / 1440), 'day');
}

/**
 * Vrais abonnés récents (GET /api/recent-members, worker.js) : seulement ceux qui ont écrit leur prénom
 * dans le champ facultatif du paiement Stripe. Liste vide = aucun toast « vient de rejoindre ».
 */
function useRecentMembers() {
  const [members, setMembers] = useState([]);
  useEffect(() => {
    let alive = true;
    fetch('/api/recent-members', { headers: { accept: 'application/json' } })
      .then((res) => (res.ok ? res.json() : { members: [] }))
      .then((data) => {
        if (!alive || !Array.isArray(data?.members)) return;
        setMembers(data.members.filter((member) => member && typeof member.name === 'string' && member.name && Date.parse(member.at)));
      })
      .catch(() => {});
    return () => { alive = false; };
  }, []);
  return members;
}

/** Prix pour les toasts, dans la devise facturée : sans ancien prix réel saisi (offer.js), rien n'est barré. */
function dealFacts({ amount, previous, perDay, money }) {
  const discount = previous !== null ? Math.floor(((previous - amount) / previous) * 100) : null;
  return {
    price: money(amount),
    perDay: money(perDay),
    previous: previous !== null ? money(previous) : null,
    discount: discount !== null ? percentLabel(discount) : null,
    saving: previous !== null ? money(previous - amount) : null,
  };
}

/**
 * Notifications en bas à gauche : créneau réel, offre (prix barré seulement s'il est réel), contenu,
 * et entre deux, les vrais membres récents s'il y en a (aucun prénom inventé).
 */
function Toasts({ copy, lang, pricing, slotLabel, onPay, disabled }) {
  const [index, setIndex] = useState(0);
  const [closed, setClosed] = useState(false);
  const members = useRecentMembers();
  const facts = { slot: slotLabel || '—', ...dealFacts(pricing) };
  const items = [];
  copy.toasts.forEach((make, at) => {
    items.push({ key: `offer-${at}`, parts: make(facts) });
    const member = members[at];
    if (member) items.push({ key: `member-${member.name}-${member.at}`, member: true, parts: copy.memberToast(member, agoLabel(member.at, lang)) });
  });
  useEffect(() => {
    const timer = window.setInterval(() => setIndex((i) => i + 1), 7000);
    return () => window.clearInterval(timer);
  }, []);
  if (closed) return null;
  const item = items[index % items.length];
  const [title, line, foot, struck] = item.parts;
  return (
    <aside className={item.member ? 'oh-toast oh-toast-member' : `oh-toast oh-toast-${index % items.length}`} key={item.key}>
      <button type="button" className="oh-toast-pay" disabled={disabled} onClick={onPay}>
        <p className="oh-toast-title">
          {item.member ? <span className="oh-toast-initial" aria-hidden="true">{line.charAt(0)}</span> : <img src={CALL_PORTRAIT} alt="" />}
          {title}
        </p>
        <p className="oh-toast-line">{struck && <s className="oh-toast-was">{struck}</s>}{line}</p>
        <p className="oh-toast-foot">OpenBro · <b>{foot}</b></p>
      </button>
      <button type="button" className="oh-toast-close" onClick={() => setClosed(true)} aria-label={copy.toastClose}>×</button>
    </aside>
  );
}

function Stories({ copy }) {
  const listRef = useRef(null);
  const videos = useRef({});

  useEffect(() => {
    const node = listRef.current;
    if (!node || typeof IntersectionObserver === 'undefined') return undefined;
    const calm = window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;
    const observer = new IntersectionObserver(([entry]) => {
      Object.values(videos.current).forEach((video) => {
        if (!video) return;
        if (entry.isIntersecting && !calm) video.play().catch(() => {});
        else if (!entry.isIntersecting) video.pause();
      });
    }, { threshold: 0.35 });
    observer.observe(node);
    return () => observer.disconnect();
  }, []);

  return (
    <ul className="oh-stories" ref={listRef}>
      {STORIES.map((story) => {
        const en = copy.lang === 'EN';
        return (
        <li key={`${story.id}-${copy.lang}`}>
          <div className="oh-story-media">
            <video
              ref={(node) => { videos.current[story.id] = node; }}
              src={en ? story.srcEn : story.src}
              poster={en ? story.posterEn : story.poster}
              muted
              loop
              playsInline
              preload="metadata"
              aria-label={`${story.name}, ${story.age}`}
            />
            <span className="oh-story-tag">{copy.stories[story.id][0]}</span>
          </div>
          <p className="oh-story-who">{story.name} - {story.age} {copy.lang === 'FR' && <FlagFR />}</p>
          <p className="oh-story-win">{copy.stories[story.id][1]}</p>
        </li>
        );
      })}
    </ul>
  );
}

export default function OpenBroV60() {
  const { lang, toggleLang } = useLang();
  const { startCheckout, isActive } = useCheckout();
  const copy = COPY[lang] || COPY.fr;
  const pricing = usePricing(lang);
  const price = pricing.money(pricing.amount);
  const minutes = callMinutes();
  const [callWindow, setCallWindow] = useState(null);
  const [openFaq, setOpenFaq] = useState(0);
  const [showBar, setShowBar] = useState(false);
  const [filmLive, setFilmLive] = useState(false);
  const [now, setNow] = useState(() => Date.now());
  const heroCtaRef = useRef(null);

  const slots = (callWindow?.slots || []).map((iso) => formatCallSlot(iso, lang));
  const spansDays = new Set(slots.map((slot) => slot.day)).size > 1;
  const selected = slots.find((slot) => slot.iso === callWindow?.selected) || slots[0] || null;

  useEffect(() => {
    const timer = window.setInterval(() => setNow(Date.now()), 1000);
    return () => window.clearInterval(timer);
  }, []);

  // Créneau passé sous la limite de réservation : on recharge une fenêtre fraîche (vrais créneaux).
  useEffect(() => {
    if (callWindow && selected && Date.parse(selected.iso) - BOOKING_LEAD_MS <= now) setCallWindow(loadCallWindow());
  }, [now, callWindow, selected]);

  useEffect(() => {
    document.documentElement.lang = lang;
    document.title = lang === 'en'
      ? 'OpenBro · Private mentorship with Gaut Bonac'
      : 'OpenBro · Le mentorat privé de Gaut Bonac';
  }, [lang]);

  useEffect(() => {
    const node = heroCtaRef.current;
    if (!node || typeof IntersectionObserver === 'undefined') return undefined;
    const observer = new IntersectionObserver(([entry]) => {
      setShowBar(!entry.isIntersecting && entry.boundingClientRect.top < 0);
    });
    observer.observe(node);
    return () => observer.disconnect();
  }, []);

  const pay = (source) => {
    if (isActive) return;
    if (isFilled(offer.paymentUrl)) {
      window.location.assign(offer.paymentUrl);
      return;
    }
    startCheckout(source, { bookLater: true });
  };

  const bookCall = (source = 'v60-call') => {
    if (isActive) return;
    if (!selected) {
      pay(source);
      return;
    }
    startCheckout(source, {
      callAt: selected.iso,
      callLabel: selected.label,
      callTz: Intl.DateTimeFormat().resolvedOptions().timeZone,
    });
  };

  const chooseSlot = (iso) => {
    setCallWindow((current) => {
      if (!current) return current;
      const next = { ...current, selected: iso };
      saveCallWindow(next);
      return next;
    });
  };

  const left = selected ? timeLeft(Date.parse(selected.iso) - BOOKING_LEAD_MS - now, copy.leftFmt) : null;
  const urgeArgs = { label: selected?.label || '', left: left || '', price };
  // Promotion (prix barré, euros seulement) : avec une vraie date de fin (offer.launchEndsAt), compte à rebours à la seconde ;
  // sans date (promotion sans fin depuis le 28/10/2026), ni date ni compte à rebours.
  const launchLeft = pricing.launchEndsAt ? timeLeft(pricing.launchEndsAt - now, copy.leftFmt) : null;
  const launch = pricing.previous !== null && (!pricing.launchEndsAt || launchLeft) ? {
    price,
    was: pricing.money(pricing.previous),
    pct: percentLabel(pricing.discount),
    left: launchLeft,
    end: pricing.launchEndsAt ? new Date(pricing.launchEndsAt).toLocaleString(lang === 'en' ? 'en-US' : 'fr-FR', { weekday: 'long', day: 'numeric', month: 'long', hour: '2-digit', minute: '2-digit' }) : null,
  } : null;
  const takeLabel = launch ? copy.ctaDeal(price, launch.was) : (selected ? copy.takeSlot(selected.label) : copy.cta(price));
  // Bloc du bas : prix du plan alimentaire (/eat), mêmes montants que le Worker (EAT_LAUNCH_CENTS / EAT_CENTS).
  const eatAmount = (now < Date.parse(offer.launchEndsAt || '') ? offer.eatPrices.launch : offer.eatPrices.after)[pricing.currency];
  const faqItems = launch ? [copy.launchFaq(launch), ...copy.faq] : copy.faq;

  /** Ligne d'urgence (créneau réel + temps restant) et, si demandé, le bouton qui le réserve tout de suite. */
  const urgeBlock = (key, source, withButton = true, extra = '') => (selected ? (
    <div className={`oh-urge-block ${extra}`}>
      {withButton && (
        <button type="button" className="oh-btn oh-btn-lg" disabled={isActive} onClick={() => bookCall(source)}>
          {isActive ? '…' : takeLabel} <Icon name="arrow" />
        </button>
      )}
      <p className="oh-urge oh-duo"><Icon name="clock" /><span><Rich text={copy.urge[key](urgeArgs)} slot={left} onSlot={() => bookCall(source)} slotDisabled={isActive} /></span></p>
    </div>
  ) : (withButton ? joinButton(source) : null));

  const joinButton = (source, extra = '') => (
    <button type="button" className={`oh-btn oh-btn-lg ${extra}`} disabled={isActive} onClick={() => pay(source)}>
      {isActive ? '…' : (launch ? copy.ctaDeal(price, launch.was) : copy.cta(price))} <Icon name="arrow" />
    </button>
  );

  return (
    <div id="top" className={filmLive ? 'oh oh-v51 oh-v52 oh-v60 oh-film-live' : 'oh oh-v51 oh-v52 oh-v60'}>
      <header className="oh-header">
        <div className="oh-wrap oh-header-row">
          <a className="oh-logo oh-logo-mark" href="#top"><img src="/logo-openbro.png" alt="OpenBro" /></a>
          <nav className="oh-nav" aria-label="OpenBro">
            {copy.nav.map(([href, label]) => <a key={href} href={href}>{label}</a>)}
          </nav>
          <div className="oh-header-actions">
            <button type="button" className="oh-lang" onClick={toggleLang} aria-label={copy.switchLabel}>
              <Icon name="globe" />{copy.lang}
            </button>
            <Link className="oh-login" to="/login">{copy.login}</Link>
            <Link className="oh-btn oh-btn-sm oh-header-login" to="/login">{copy.headerLogin}</Link>
            <button type="button" className="oh-btn oh-btn-sm oh-header-book" disabled={isActive} onClick={() => bookCall('v60-header')}>{selected ? copy.headerSlot(selected.time) : copy.headerCta}</button>
          </div>
        </div>
      </header>

      <section className="oh-hero">
        <div className="oh-hero-bg" aria-hidden="true" style={{ backgroundImage: 'url(/v3-board.jpg)' }} />
        <div className="oh-wrap oh-hero-grid">
          <div className="oh-hero-copy">
            <p className="oh-eyebrow">{copy.eyebrow}</p>
            <h1><Rich text={copy.title} /></h1>
            <p className="oh-lead oh-duo"><Rich text={copy.lead} /></p>
            <p className="oh-between oh-duo"><Rich text={copy.between} /></p>
            <Proof lang={lang} copy={copy} />
            {launch && (
              <button type="button" className="oh-launch" disabled={isActive} onClick={() => bookCall('v60-launch')}>
                <span className="oh-launch-pill">{copy.launchPill(launch.pct)}</span>
                <span className="oh-launch-line"><Rich text={copy.launchLine(launch)} /></span>
              </button>
            )}
            {selected && (
              <button type="button" className="oh-urge oh-urge-hero oh-duo" disabled={isActive} onClick={() => bookCall('v60-hero-urge')}>
                <Icon name="clock" /><span><Rich text={copy.heroUrge(urgeArgs)} /></span>
              </button>
            )}
            <div className="oh-hero-ctas">
              <button type="button" ref={heroCtaRef} className="oh-btn oh-btn-lg" disabled={isActive} onClick={() => bookCall('v60-hero')}>
                {isActive ? '…' : takeLabel} <Icon name="arrow" />
              </button>
              <a className="oh-ghost" href="#chemins">{copy.secondary}</a>
            </div>
            {selected && <p className="oh-urge-sub oh-duo"><Rich text={copy.urge.hero(urgeArgs)} slot={left} onSlot={() => bookCall('v60-hero-sub')} slotDisabled={isActive} /></p>}
            <p className="oh-later">{copy.bookLater}</p>
            <ul className="oh-reassure">
              {copy.reassure.map((item) => <li key={item}><Icon name="check" />{item}</li>)}
            </ul>
          </div>

        </div>
      </section>

      {/* Vidéo sous le hero (28/10/2026) : tout en haut, les visiteurs la dépassaient sans la voir. id gardé pour le cockpit. */}
      <section className="oh-top-film" id="film-top">
        <div className="oh-wrap">
          <OpenBroFullFilm src={FILM[lang] || FILM.fr} poster={FILM_POSTER[lang] || FILM_POSTER.fr} playLabel={copy.filmPlay} labels={copy.player} onLive={setFilmLive} />
        </div>
      </section>

      {/* V60 : les 6 modules tout de suite après l’offre */}
      <section className="oh-section oh-alt oh-formations-top" id="formations">
        <div className="oh-wrap">
          <Pill icon="book">{copy.formationsPill}</Pill>
          <h2 className="oh-h2 oh-duo-h"><Rich text={copy.formationsTitle} /></h2>
          <p className="oh-sub oh-duo"><Rich text={copy.formationsBody} /></p>
          <ul className="oh-formations">
            {FORMATIONS.map((item) => {
              const [title, text] = copy.formations[item.id];
              return (
                <li key={item.id}>
                  <div className="oh-formation-media">
                    {item.verified && <VerifiedBadge label={copy.verified} />}
                    {item.video ? (
                      <video src={item.src} poster={item.poster} muted autoPlay loop playsInline preload="metadata" style={{ objectPosition: item.focus }} />
                    ) : (
                      <img src={item.src} alt="" loading="lazy" decoding="async" style={{ objectPosition: item.focus }} />
                    )}
                  </div>
                  <strong>{title}</strong>
                  <span>{text}</span>
                </li>
              );
            })}
          </ul>
        </div>
      </section>

      {/* Réf. 02 : paragraphe bicolore + vraie photo */}
      <section className="oh-section" id="verite">
        <div className="oh-wrap oh-split">
          <div>
            <Pill>{copy.truthPill}</Pill>
            <h2 className="oh-h2 oh-duo-h"><Rich text={copy.truthTitle} /></h2>
            <div className="oh-duo oh-body">
              {copy.truth.map((text) => <p key={text}><Rich text={text} /></p>)}
            </div>
            {urgeBlock('truth', 'v60-truth')}
          </div>
          <figure className="oh-photo oh-photo-tall">
            <img src="/v10/portrait-tentations-full.jpg" alt="" loading="lazy" decoding="async" />
          </figure>
        </div>
      </section>

      <section className="oh-section oh-alt oh-stories-section" id="resultats">
        <div className="oh-wrap">
          <h2 className="oh-h2 oh-center oh-duo-h"><Rich text={copy.storiesTitle} /></h2>
          <Stories copy={copy} />
          {urgeBlock('stories', 'v60-stories', true, 'oh-urge-center')}
        </div>
      </section>

      {/* Réf. 03 : deux chemins, un vrai bouton de chaque côté */}
      <section className="oh-section" id="chemins">
        <div className="oh-wrap">
          <div className="oh-center-head">
            <Pill>{copy.pathsPill}</Pill>
            <h2 className="oh-h2 oh-center oh-vs-title"><Rich text={copy.pathsTitle} /></h2>
          </div>
          <div className="oh-paths">
            <article className="oh-path oh-path-off">
              <h3>{copy.aloneTitle}</h3>
              <ul>{copy.alone.map((item) => <li key={item}><Icon name="cross" />{item}</li>)}</ul>
              <a className="oh-btn oh-btn-dim oh-btn-block" href={copy.aloneHref} target="_blank" rel="noopener noreferrer">{copy.aloneCta}</a>
            </article>
            <span className="oh-vs" aria-hidden="true">vs</span>
            <article className="oh-path oh-path-on">
              <h3><span className="oh-logo">OpenBro<span>✦</span></span></h3>
              <ul>{copy.with.map((item) => <li key={item}><Icon name="check" />{item}</li>)}</ul>
              {selected ? (
                <button type="button" className="oh-btn oh-btn-lg oh-btn-block" disabled={isActive} onClick={() => bookCall('v60-paths')}>
                  {isActive ? '…' : takeLabel} <Icon name="arrow" />
                </button>
              ) : joinButton('v60-paths', 'oh-btn-block')}
              {urgeBlock('paths', 'v60-paths', false)}
            </article>
          </div>
        </div>
      </section>

      {/* Réf. 04 : bento, une idée forte + une image réelle */}
      <section className="oh-section oh-alt" id="methode">
        <div className="oh-wrap oh-bento">
          <article className="oh-bento-text">
            <Pill icon="clock">{copy.timePill}</Pill>
            <h2 className="oh-h3 oh-duo-h"><Rich text={copy.timeTitle} /></h2>
            <div className="oh-duo oh-body">
              {copy.time.map((text) => <p key={text}><Rich text={text} /></p>)}
            </div>
            {urgeBlock('time', 'v60-time', false)}
          </article>
          <figure className="oh-bento-media">
            <img src="/cover-train.jpg" alt="" loading="lazy" decoding="async" style={{ objectPosition: "center 30%" }} />
          </figure>
          <figure className="oh-bento-media oh-bento-studio">
            <img src="/v10/home-macbook.jpg" alt="" loading="lazy" decoding="async" />
          </figure>
          <article className="oh-bento-text">
            <Pill icon="plan">{copy.planPill}</Pill>
            <h2 className="oh-h3 oh-duo-h"><Rich text={copy.planTitle} /></h2>
            <div className="oh-duo oh-body">
              {copy.plan.map((text) => <p key={text}><Rich text={text} /></p>)}
            </div>
          </article>
        </div>
      </section>

      {/* Réf. 05 : titre court, un bouton, un vrai téléphone */}
      <section className="oh-section oh-phone-section" id="appel">
        <div className="oh-wrap oh-split">
          <div>
            <Pill icon="call">{copy.callPill}</Pill>
            <h2 className="oh-h2 oh-h2-xl oh-duo-h"><Rich text={selected ? copy.callTitleSlot(selected.label) : copy.callTitle} /></h2>
            <div className="oh-duo oh-body">
              {copy.call(minutes).map((text) => <p key={text}><Rich text={text} /></p>)}
            </div>
            {urgeBlock('call', 'v60-phone')}
          </div>
          <div className="oh-phone-stage">
            <PhoneShot alt="" />
          </div>
        </div>
      </section>

      {/* Réf. 07 : mur façon « Our Students Are Winning », en cartes de messages HTML */}
      <section className="oh-section oh-alt" id="bros">
        <div className="oh-wrap">
          <div className="oh-center-head">
            <Pill>{copy.winsPill}</Pill>
            <h2 className="oh-h2 oh-center oh-duo-h"><Rich text={copy.winsTitle} /></h2>
            <p className="oh-sub oh-center oh-duo"><Rich text={copy.winsBody} /></p>
          </div>
          <ul className="oh-tg-wall">
            {WINS.map((msg) => (msg.shot ? (
              <li key={msg.shot} className="oh-win-shot">
                <img src={msg.shot} alt="" loading="lazy" decoding="async" />
              </li>
            ) : (
              <li key={msg.id} className="oh-tg">
                <img className="oh-tg-avatar" src={msg.avatar} alt="" loading="lazy" />
                <div className="oh-tg-bubble">
                  <p className="oh-tg-head">
                    <strong style={{ color: msg.color }}>{msg.name}</strong>
                    <span>{copy.groupName}</span>
                  </p>
                  {msg.photo && <img className="oh-tg-photo" src={msg.photo} alt="" loading="lazy" decoding="async" />}
                  <p className="oh-tg-text">{copy.wins[msg.id]}<time>{msg.time}</time></p>
                  <p className="oh-tg-reacts">{msg.reactions.map(([emoji, count]) => {
                    const gSrc = emoji === 'G' ? '/emoji-g.png' : emoji === 'G2' ? '/emoji-g2.png' : null;
                    return (
                      <span key={emoji} className={gSrc ? 'is-g' : ''}>
                        {gSrc ? <img src={gSrc} alt="" /> : emoji}
                        <em>{count}</em>
                      </span>
                    );
                  })}</p>
                </div>
              </li>
            )))}
          </ul>
          <p className="oh-results-note">{copy.resultsNote}</p>
          {urgeBlock('wins', 'v60-wins', true, 'oh-urge-center')}
        </div>
      </section>

      {/* Campus : visuels de l'espace membre, professeurs non nommés + écran flouté */}
      <section className="oh-section" id="campus">
        <div className="oh-wrap">
          <div className="oh-center-head">
            <Pill icon="book">{copy.campusPill}</Pill>
            <h2 className="oh-h2 oh-center oh-duo-h"><Rich text={copy.campusTitle} /></h2>
          </div>
          <ul className="oh-campus">
            {CAMPUS.map((item) => {
              const title = copy.campus[item.id];
              return (
                <li key={item.id}>
                  <img src={lang === 'en' && item.srcEn ? item.srcEn : item.src} alt={title} loading="lazy" decoding="async" />
                  <div className="oh-campus-info">
                    <strong>{title}</strong>
                    <em>{copy.lessons(item.lessons)}</em>
                  </div>
                </li>
              );
            })}
          </ul>
          <VaultScreen copy={copy} disabled={isActive} onPay={() => bookCall('v60-vault')} />
        </div>
      </section>

      {/* Réf. 08 : texte à gauche, deux visuels réels à droite */}
      <section className="oh-section" id="mentor">
        <div className="oh-wrap oh-split oh-split-mentor">
          <div>
            <Pill>{copy.mentorPill}</Pill>
            <h2 className="oh-h2 oh-duo-h"><Rich text={copy.mentorTitle} /></h2>
            <div className="oh-duo oh-body">
              {copy.mentor.map((text) => <p key={text}><Rich text={text} /></p>)}
            </div>
            <p className="oh-sign">— {copy.mentorSign}</p>
            {urgeBlock('mentor', 'v60-mentor')}
          </div>
          <div className="oh-duo-photos" aria-hidden="true">
            <img src="/portrait-gaut.jpg" alt="" loading="lazy" decoding="async" />
            <img src={CALL_PORTRAIT} alt="" loading="lazy" decoding="async" />
          </div>
        </div>
      </section>

      {/* Réf. 09 : dernier choix, ne rien faire vs agir, avec le prix */}
      <section className="oh-section oh-alt" id="prix">
        <div className="oh-wrap">
          <h2 className="oh-h2 oh-center oh-duo-h"><Rich text={copy.choiceTitle} /></h2>
          <div className="oh-choice">
            <article className="oh-choice-card oh-choice-off">
              <span className="oh-choice-ico"><Icon name="clock" /></span>
              <h3>{copy.nothingTitle}</h3>
              <p className="oh-choice-sub">{copy.nothingSub}</p>
              <p className="oh-choice-cost">{copy.nothingCost}</p>
              <ul>{copy.nothing.map((item) => <li key={item}><Icon name="cross" />{item}</li>)}</ul>
              <a className="oh-btn oh-btn-dim oh-btn-block" href={copy.nothingHref} target="_blank" rel="noopener noreferrer">{copy.nothingCta}</a>
            </article>
            <article className="oh-choice-card oh-choice-on">
              <span className="oh-choice-ico"><Icon name="flame" /></span>
              <h3>{copy.actTitle}</h3>
              <p className="oh-choice-sub">{copy.actSub}</p>
              <div className="oh-choice-price">
                <PriceTag copy={copy} pricing={pricing} big />
                <p className="oh-perday">{copy.perDay(pricing.money(pricing.perDay))}</p>
              </div>
              <ul>{copy.perks.map((perk) => <li key={perk}><Icon name="check" />{perk}</li>)}</ul>
              {selected ? (
                <button type="button" className="oh-btn oh-btn-lg oh-btn-block" disabled={isActive} onClick={() => bookCall('v60-price')}>
                  {isActive ? '…' : takeLabel} <Icon name="arrow" />
                </button>
              ) : joinButton('v60-price', 'oh-btn-block')}
              {urgeBlock('price', 'v60-price', false)}
              <p className="oh-fine">{copy.priceFine(price)}</p>
              <p className="oh-secure"><Icon name="lock" />{copy.secure}<PayMarks currency={pricing.currency} /></p>
              <SolanaPayButton lang={lang} className="oh-crypto" label={copy.crypto} />
            </article>
          </div>
        </div>
      </section>

      <section className="oh-section" id="faq">
        <div className="oh-wrap oh-faq-grid">
          <div>
            <Pill>{copy.faqPill}</Pill>
            <h2 className="oh-h2 oh-duo-h"><Rich text={copy.faqTitle} /></h2>
            <p className="oh-sub">{copy.faqMore} <a href={`mailto:${SUPPORT_EMAIL}`}>{SUPPORT_EMAIL}</a></p>
          </div>
          <div className="oh-faq">
            {faqItems.map(([question, answer], index) => {
              const open = openFaq === index;
              return (
                <div key={question} className={open ? 'oh-qa is-open' : 'oh-qa'}>
                  <button type="button" aria-expanded={open} onClick={() => setOpenFaq(open ? null : index)}>
                    {question}<Icon name="plus" />
                  </button>
                  {open && <p className="oh-duo"><Rich text={typeof answer === 'function' ? answer(price) : answer} /></p>}
                </div>
              );
            })}
          </div>
        </div>
      </section>

      <section className="oh-final">
        <div className="oh-wrap">
          <h2 className="oh-duo-h"><Rich text={copy.finalTitle} /></h2>
          <Pill>{copy.finalPill}</Pill>
          <p className="oh-final-bro oh-duo"><Rich text={copy.finalBro} /></p>
          {urgeBlock('final', 'v60-final', true, 'oh-urge-center')}
          <ul className="oh-reassure oh-reassure-center">
            {copy.reassure.map((item) => <li key={item}><Icon name="check" />{item}</li>)}
          </ul>
        </div>
      </section>

      {/* Tout en bas (28/10/2026) : le plan alimentaire seul, l'inverse de la passerelle « OpenBro Elite » de /eat. */}
      <section className="oh-section oh-alt oh-eatplan" id="plan-alimentaire">
        <div className="oh-wrap oh-eatplan-row">
          <a className="oh-eatplan-photos" href="/eat" tabIndex={-1} aria-hidden="true">
            {EAT_PLAN_PHOTOS.map((photo) => <img key={photo.src} src={photo.src} alt="" loading="lazy" decoding="async" style={{ objectPosition: photo.pos }} />)}
          </a>
          <div>
            <Pill>{copy.eatPlan.pill}</Pill>
            <h2 className="oh-h2 oh-duo-h"><Rich text={copy.eatPlan.title} /></h2>
            <p className="oh-sub oh-duo"><Rich text={copy.eatPlan.text} /></p>
            <p className="oh-eatplan-price"><strong>{pricing.money(eatAmount)}</strong> {copy.eatPlan.perMonth}</p>
            <a className="oh-btn oh-eatplan-btn" href="/eat">{copy.eatPlan.cta} <Icon name="arrow" /></a>
          </div>
        </div>
      </section>

      <footer className="oh-footer">
        <div className="oh-wrap oh-footer-row">
          <div>
            <p className="oh-logo">OpenBro<span>✦</span></p>
            <p>{copy.footerTag}</p>
          </div>
          <nav aria-label="Legal">
            <Link to="/terms">{copy.terms}</Link>
            <Link to="/privacy">{copy.privacy}</Link>
            <Link to="/login">{copy.login}</Link>
            <a href={`mailto:${SUPPORT_EMAIL}`}>{SUPPORT_EMAIL}</a>
          </nav>
          <p className="oh-copy">© {new Date().getFullYear()} OpenBro. {copy.rights}</p>
        </div>
      </footer>

      <div className={showBar ? 'oh-bar is-on' : 'oh-bar'} aria-hidden={!showBar}>
        {selected ? (
          <span className="oh-bar-slot">
            <em>{copy.barSlot}</em>
            <strong>{selected.label}</strong>
            {left && (
              <button type="button" className="oh-bar-until" tabIndex={showBar ? 0 : -1} disabled={isActive} onClick={() => bookCall('v60-bar-until')}>
                {copy.barUntil(left)}
              </button>
            )}
          </span>
        ) : (
          <span className="oh-bar-slot">
            <PriceTag copy={copy} pricing={pricing} />
            {launch?.left && <small className="oh-bar-launch">{copy.barLaunch(launch.left)}</small>}
          </span>
        )}
        <button type="button" className="oh-btn" tabIndex={showBar ? 0 : -1} disabled={isActive} onClick={() => bookCall('v60-bar')}>
          {isActive ? '…' : (selected ? `${copy.barTake} · ${price}` : copy.ctaShort)} <Icon name="arrow" />
        </button>
      </div>

      <Toasts copy={copy} lang={lang} pricing={pricing} slotLabel={selected?.label} disabled={isActive} onPay={() => bookCall('v60-toast')} />
      <style>{'html { scroll-behavior: smooth; } @media (prefers-reduced-motion: reduce) { html { scroll-behavior: auto; } }'}</style>
    </div>
  );
}
